import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { CartItem } from '@softapps/wws/pos-core';
import { ProductRepository } from '../repositories/product.repository';

@Injectable()
export class ProductService {
  readonly productRepository = inject(ProductRepository);

  readonly categoryProductMap = new Map<number, CartItem[]>();

  /* PRIVATE API */
  readonly #categories = resource({
    params: () => ({}),
    loader: async () => {
      const result = await this.productRepository.loadCategories();

      this.#categoryId.set(result[0].id ?? undefined);

      return result;
    },
  });

  readonly #productsResource = resource({
    params: () => this.#categoryId(),
    loader: async ({ params }) => {
      const categoryId = params;
      if (!categoryId) {
        return null;
      }

      if (this.categoryProductMap.has(categoryId))
        return this.categoryProductMap.get(categoryId);

      const result = await this.productRepository.loadProductsByCategoryId(categoryId);
      this.categoryProductMap.set(categoryId, result);

      return result;
    },
  });

  /* ids */
  readonly #categoryId = signal<number | undefined>(undefined);

  /* PUBLIC API */
  readonly categoriesLoading = computed(() => this.#categories.isLoading());
  readonly productsLoading = computed(() => this.#productsResource.isLoading());
  readonly selectedCategoryId = computed(() => this.#categoryId());
  readonly selectedCategory = computed(() => {
    return this.categories().find((c) => c.id === this.selectedCategoryId());
  });

  // `.value()` throws on an errored resource — gate it so a failed load can't loop.
  readonly categoriesError = computed(() => this.#categories.status() === 'error');
  readonly productsError = computed(() => this.#productsResource.status() === 'error');

  // Signals with data
  readonly categories = computed(() =>
    this.#categories.hasValue()
      ? (this.#categories.value() ?? []).sort((a, b) => a.sortNumber - b.sortNumber)
      : []
  );

  readonly products = linkedSignal(() =>
    this.#productsResource.hasValue() ? (this.#productsResource.value() ?? []) : []
  );

  selectCategory(id: number | undefined): void {
    this.#categoryId.set(id);
  }

  // Loading methods
  loadProducts(categoryId: number) {
    this.#categoryId.set(categoryId);
  }

  // Search methods
  searchProducts(value: string | null | undefined): void {
    const products = this.#productsResource.hasValue()
      ? (this.#productsResource.value() ?? [])
      : [];
    if (!value) {
      this.products.set(products);
      return;
    }

    const searchText = value.toLowerCase();
    const result = products.filter((item) => {
      return item.product.description?.description.toLowerCase().includes(searchText);
    });

    this.products.set(result);
    return;
  }
}
